import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Plus, Trash2, SplitSquareHorizontal } from "lucide-react";
import { adminClient } from "./api";
import ImageUploader from "./ImageUploader";

const EMPTY = { title: "", before_url: "", after_url: "", service_type: "junk", caption: "", sort_order: 0, is_active: true };
const SERVICES = ["junk", "drywall", "furniture", "handyman"];

export default function BeforeAfterAdmin() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = useCallback(async () => {
    try { const { data } = await adminClient.get("/admin/before-after"); setItems(data.items || []); }
    catch { toast.error("Could not load before/after photos"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(null); setForm({ ...EMPTY, sort_order: items.length }); setOpen(true); };
  const openEdit = (it) => { setEditing(it); setForm({ ...it }); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    if (!form.before_url || !form.after_url) return toast.error("Upload both a before and an after photo");
    if (!form.title.trim()) return toast.error("Give it a short title");
    const payload = { ...form, sort_order: parseInt(form.sort_order, 10) || 0, is_active: !!form.is_active };
    delete payload.id; delete payload.created_at;
    try {
      if (editing) await adminClient.put(`/admin/before-after/${editing.id}`, payload);
      else await adminClient.post("/admin/before-after", payload);
      toast.success("Saved · Live on site now");
      setOpen(false); load();
    } catch { toast.error("Save failed"); }
  };

  const remove = async (it) => {
    if (!window.confirm(`Delete "${it.title}"?`)) return;
    try { await adminClient.delete(`/admin/before-after/${it.id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <div data-testid="before-after-admin" className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display font-black text-3xl text-[#0C2E2B] flex items-center gap-2"><SplitSquareHorizontal className="w-7 h-7" /> Before & After</h1>
          <p className="text-sm text-zinc-500 mt-1">Real jobs, shown with a drag slider on the homepage. Shoot both photos from the same spot for the best effect.</p>
        </div>
        <Button data-testid="ba-add-btn" onClick={openCreate} className="btn-kinetic rounded-md"><Plus className="w-4 h-4 mr-1" /> Add pair</Button>
      </div>

      {items.length === 0 ? (
        <Card className="p-10 text-center text-zinc-500 text-sm">No before/after pairs yet. Add your first finished job.</Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {items.map((it) => (
            <Card key={it.id} data-testid={`ba-card-${it.id}`} className={`overflow-hidden border-2 ${it.is_active ? "border-zinc-200" : "border-dashed border-zinc-300 opacity-60"}`}>
              <div className="grid grid-cols-2 h-36">
                <img src={it.before_url} alt="Before" className="w-full h-full object-cover" />
                <img src={it.after_url} alt="After" className="w-full h-full object-cover" />
              </div>
              <div className="p-3">
                <div className="flex items-center justify-between gap-2">
                  <div className="font-bold text-sm text-[#0C2E2B] truncate">{it.title}</div>
                  <span className="text-[10px] uppercase tracking-widest text-zinc-400">#{it.sort_order} · {it.service_type}</span>
                </div>
                {it.caption && <p className="text-xs text-zinc-500 mt-1 line-clamp-2">{it.caption}</p>}
                <div className="flex gap-2 mt-3">
                  <Button data-testid={`ba-edit-${it.id}`} size="sm" variant="outline" className="h-7 text-xs rounded-md" onClick={() => openEdit(it)}>Edit</Button>
                  <Button data-testid={`ba-delete-${it.id}`} size="sm" variant="outline" className="h-7 text-xs rounded-md text-red-600" onClick={() => remove(it)}><Trash2 className="w-3 h-3" /></Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">{editing ? "Edit before/after" : "New before/after"}</DialogTitle>
            <DialogDescription>Both photos are required. Visitors drag a handle to compare them.</DialogDescription>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4" data-testid="ba-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-bold uppercase tracking-widest text-zinc-700">Before</Label>
                <ImageUploader value={form.before_url} onChange={(u) => setForm({ ...form, before_url: u })} testId="ba-before-uploader" />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-widest text-zinc-700">After</Label>
                <ImageUploader value={form.after_url} onChange={(u) => setForm({ ...form, after_url: u })} testId="ba-after-uploader" />
              </div>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-widest text-zinc-700">Title</Label>
              <Input data-testid="ba-form-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Garage cleanout · Edina" className="mt-1" required />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs font-bold uppercase tracking-widest text-zinc-700">Service</Label>
                <select data-testid="ba-form-service" value={form.service_type} onChange={(e) => setForm({ ...form, service_type: e.target.value })} className="mt-1 w-full h-10 rounded-md border border-zinc-300 bg-white px-3 text-sm">
                  {SERVICES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-widest text-zinc-700">Order</Label>
                <Input data-testid="ba-form-order" type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: e.target.value })} className="mt-1" />
              </div>
              <div className="flex items-center gap-2 pt-6">
                <Switch data-testid="ba-form-active" checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} />
                <span className="text-xs font-bold">Show on site</span>
              </div>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-widest text-zinc-700">Caption (optional)</Label>
              <Input data-testid="ba-form-caption" value={form.caption} onChange={(e) => setForm({ ...form, caption: e.target.value })} placeholder="Half a truck hauled, swept, and done in 90 minutes" className="mt-1" />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-md">Cancel</Button>
              <Button data-testid="ba-form-save" type="submit" className="btn-kinetic rounded-md">Save</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
